
import React from "react";
import { FileIcon, FileText, ImageIcon, Mail, Phone, Video } from "lucide-react";
import { showSuccess, showError } from "@/shared/utils/toast";
import dayjs from "dayjs";
import LinkedinSquare from "@/assets/svg/Linkedin-square.svg";
import LinkedinCircular from "@/assets/svg/linkedin-circular.svg";
import { CHANNEL_STEPS } from "./channelConfig";
import { USER_ROLES } from "@/shared/utils/constants";
import whatsappIcon from "@/assets/svg/whatsapp.svg";

/** Lets /pricing RouteGuard allow one navigation right after onboarding (avoids stale isOnboarded in first paint). */
const SESSION_FORCE_PRICING = "sw_force_pricing_after_onboarding";

export function setForcePostOnboardingPricing() {
  try {
    sessionStorage.setItem(SESSION_FORCE_PRICING, "1");
  } catch {
    /* ignore */
  }
}

function peekForcePostOnboardingPricing() {
  try {
    return sessionStorage.getItem(SESSION_FORCE_PRICING) === "1";
  } catch {
    return false;
  }
}

/** Call once when Pricing has mounted so the flag does not linger. */
export function clearForcePostOnboardingPricing() {
  try {
    sessionStorage.removeItem(SESSION_FORCE_PRICING);
  } catch {
    /* ignore */
  }
}

// Single source of truth: where an authenticated user should land.

export function getTargetRoute(user, options = {}) {
  if (!user?.id) return "/onboarding";

  if (user.approvalStatus === "rejected") return "/join-organization/rejected";

  const isSuperAdmin =
    user.role === USER_ROLES.SUPER_ADMIN ||
    user.role === USER_ROLES.SUPER_ADMIN_ALT;
  if (isSuperAdmin) return "/sa/dashboard";

  const appMode = options.appMode ?? "user";

  if (user.role === USER_ROLES.ORG_ADMIN && appMode === "user") {
    if (!user.isOnboarded) return "/onboarding";
    if (user.approvalStatus === "pending") {
      return user.hasAccessRequest
        ? "/join-organization/status"
        : "/admin-request";
    }
    if (user.approvalStatus === "approved") return "/campaigns";
    return "/admin-request";
  }

  if (user.role === USER_ROLES.ORG_ADMIN) {
    if (!user.isOnboarded) return "/onboarding";
    if (user.approvalStatus === "pending") {
      return user.hasAccessRequest
        ? "/join-organization/status"
        : "/admin-request";
    }
    if (user.approvalStatus === "approved") return "/oa/dashboard";
    return "/admin-request";
  }

  if (!user.isOnboarded) return "/onboarding";
  if (user.approvalStatus === "approved") return "/campaigns";
  if (user.approvalStatus === "pending") {
    return user.hasAccessRequest
      ? "/join-organization/status"
      : "/join-organization";
  }
  return "/join-organization";
}

// Whether /pricing may render. Allows one mandatory view after onboarding even if

export function mayAccessPostOnboardingPricing(user, options = {}) {
  if (peekForcePostOnboardingPricing()) return true;
  if (!user?.id || !user?.isOnboarded) return false;
  if (user.approvalStatus === "rejected") return false;
  const isSuper =
    user.role === USER_ROLES.SUPER_ADMIN ||
    user.role === USER_ROLES.SUPER_ADMIN_ALT;
  if (isSuper) return false;
  if (user.approvalStatus === "approved") {
    return getTargetRoute(user, options) === "/pricing";
  }
  if (user.pricingPageCompleted !== true) {
    return true;
  }
  return getTargetRoute(user, options) === "/pricing";
}
// add bullet point in lines
export const addBulletPoints = (line) => {
  if (!line || typeof line !== "string") {
    return "";
  }
  const lines = line.split("\n");
  const bulletedLines = lines.map((line) => {
    if (line.trim() === "" || line.trim().startsWith("•")) {
      return line;
    }
    return "• " + line;
  });
  return bulletedLines.join("\n");
};

// get name initials from email
export const getInitials = (fallbackEmail = "") => {
  if (fallbackEmail) {
    const [local, domain] = fallbackEmail.split("@");
    return `${local?.[0] ?? ""}${domain?.[0] ?? ""}`.toUpperCase();
  }
  return "";
};

// get name initials from name
export const getName = (name = "") => {
  if (!name || typeof name !== "string") return "";

  const trimmed = name.trim();
  if (!trimmed) return "";
  // An email shown as a name would otherwise take its last initial from ".com".
  if (trimmed.includes("@")) return getInitials(trimmed);

  const STOP_WORDS = ["from", "at", "of", "the", "and", "by", "for"];

  // Replace punctuation like dots, dashes, underscores, slashes with spaces
  const parts = trimmed
    .replace(/[.\-_/,]+/g, " ")
    .split(/\s+/)
    .filter(Boolean);

  if (parts.length === 0) return trimmed[0].toUpperCase();

  // If all parts are single characters (e.g., "J", "B", "C" from "J.B.C" or "J-B-C")
  const isAllSingleLetters = parts.every(
    (p) => p.length === 1 && /[a-zA-Z0-9]/.test(p),
  );
  if (isAllSingleLetters) {
    return parts
      .map((p) => p.toUpperCase())
      .join("")
      .slice(0, 3);
  }

  // Filter stop words if we have multiple words
  const words = parts.filter((w) => !STOP_WORDS.includes(w.toLowerCase()));
  const effectiveWords = words.length > 0 ? words : parts;

  if (effectiveWords.length >= 2) {
    const firstInitial = effectiveWords[0][0].toUpperCase();
    const lastInitial =
      effectiveWords[effectiveWords.length - 1][0].toUpperCase();
    return `${firstInitial}${lastInitial}`;
  }

  // Single word: check if camelCase or multiple capital letters exist (e.g., "JBC" or "McDonald")
  const single = effectiveWords[0];
  const capMatches = single.match(/[A-Z]/g);
  if (capMatches && capMatches.length >= 2) {
    return capMatches.slice(0, 2).join("");
  }

  return single[0].toUpperCase();
};


// blob to base 64
export const blobToBase64 = (blob) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });

// get variant based on number
export const getVariant = (number) => {
  if (typeof number !== "number") return "neutral";
  if (number >= 75) {
    return "success";
  } else if (number >= 50) {
    return "warning";
  } else {
    return "danger";
  }
};

export const getEngagementStatusVariant = (status) => {
  const normalized = String(status || "").toLowerCase();
  if (normalized === "hot" || normalized === "engaged") return "success";
  if (normalized === "warm") return "warning";
  if (normalized === "cold") return "danger";
  return "neutral";
};

const formatMillionsShort = (millions, currency = "$") => {
  if (!Number.isFinite(millions) || millions <= 0) return null;

  const trim = (num) => String(num).replace(/\.0$/, "");

  if (millions >= 1_000_000) {
    const trillions = millions / 1_000_000;
    const decimals = trillions >= 10 ? 0 : 1;
    return `${currency}${trim(trillions.toFixed(decimals))}T`;
  }

  if (millions >= 1000) {
    const billions = millions / 1000;
    const decimals = billions >= 10 ? 0 : 1;
    return `${currency}${trim(billions.toFixed(decimals))}B`;
  }

  if (millions >= 1) {
    const decimals = millions >= 100 ? 0 : 1;
    return `${currency}${trim(millions.toFixed(decimals))}M`;
  }

  const thousands = millions * 1000;
  return `${currency}${trim(thousands.toFixed(thousands >= 100 ? 0 : 1))}K`;
};

/** Revenue is stored in millions (e.g. 6000 → $6B, 150 → $150M) or raw amounts. Returns null for 0/unknown. */
export const formatAccountRevenue = (value) => {
  if (value == null || value === "") return null;
  const num = Number(value);
  if (!Number.isFinite(num) || num <= 0) return null;
  const millions = num >= 100_000 ? num / 1_000_000 : num;
  return formatMillionsShort(millions, "$");
};

const REVENUE_UNIT_TO_MILLIONS = {
  trillion: 1_000_000,
  tn: 1_000_000,
  t: 1_000_000,
  billion: 1000,
  billions: 1000,
  bn: 1000,
  b: 1000,
  crore: 10,
  crores: 10,
  cr: 10,
  million: 1,
  millions: 1,
  mn: 1,
  m: 1,
  lakh: 0.1,
  lakhs: 0.1,
  thousand: 0.001,
  thousands: 0.001,
  k: 0.001,
};

/**
 * Render revenue as a short currency figure ("$1.2M", "₹3B") with no
 * surrounding text. Accepts numeric format (millions USD), research
 * objects ({ value }) and display strings like "USD 6.9 billion (FY2024)" or
 * "₹20,054 crore".
 */
export const formatRevenueShort = (
  value,
  { fallback = ACCOUNT_REVENUE_UNAVAILABLE } = {},
) => {
  if (value == null || value === "" || value === "null" || value === "undefined") return fallback;
  if (typeof value === "object") {
    const innerVal = value?.value ?? value?.amount ?? value?.revenue;
    return formatRevenueShort(innerVal, { fallback });
  }
  if (typeof value === "number") return formatAccountRevenue(value) || fallback;

  const strVal = String(value).trim();
  if (!strVal || strVal.toLowerCase() === "not identified" || strVal.toLowerCase() === "n/a") {
    return fallback;
  }

  const text = strVal
    .replace(/\bINR\b/gi, "₹")
    .replace(/\bRs\.?\b/gi, "₹")
    .replace(/\bUSD\b/gi, "$")
    .replace(/\bEUR\b/gi, "€")
    .replace(/\bGBP\b/gi, "£")
    .trim();

  const originalCurrencyMatch = text.match(/[₹$€£]/);
  const currencySymbol = originalCurrencyMatch ? originalCurrencyMatch[0] : "$";
  const cleaned = text.replace(/,/g, "");

  // Prefer a number that carries a scale unit; fall back to the first number.
  const withUnit = cleaned.match(
    /(\d+(?:\.\d+)?)\s*(trillion|billions?|millions?|crores?|lakhs?|thousands?|tn|bn|mn|cr|[tbmk])\b/i,
  );
  const numeric = withUnit || cleaned.match(/(\d+(?:\.\d+)?)/);
  if (!numeric) return fallback;

  const amount = Number(numeric[1]);
  if (!Number.isFinite(amount) || amount <= 0) return fallback;

  const unit = (numeric[2] || "").toLowerCase();
  let millions;
  if (unit && REVENUE_UNIT_TO_MILLIONS[unit] !== undefined) {
    millions = amount * REVENUE_UNIT_TO_MILLIONS[unit];
  } else if (amount >= 100000) {
    // Large bare figure — treat as an absolute currency amount.
    millions = amount / 1_000_000;
  } else {
    // Unitless figures follow the stored convention: value in millions.
    millions = amount;
  }

  return formatMillionsShort(millions, currencySymbol) || fallback;
};

/** Bare people count with no words: "About 38,655 employees (FY25)" → "38,655". */
export const formatPeopleCountShort = (
  value,
  { fallback = ACCOUNT_REVENUE_UNAVAILABLE } = {},
) => {
  if (value == null || value === "") return fallback;
  const text = String(value)
    .replace(/[~≈]/g, " ")
    .replace(/\bto\b/gi, "-");
  const match = text.match(/\d[\d,.]*(?:\s*[-–—]\s*\d[\d,.]*)?\+?/);
  if (!match) return fallback;
  return match[0]
    .replace(/\s*([-–—])\s*/g, "$1")
    .replace(/[.,]+$/, "")
    .trim();
};

/**
 * New-format (contact company research) profiles drive the card-based
 * Account 360 layout; any populated new-format section qualifies, so partially
 * filled research doesn't fall back to the legacy layout it can't render.
 */
export const isNewFormatAccountProfile = (profile) => {
  if (!profile || typeof profile !== "object") return false;
  const hasContent = (field) => {
    if (field == null) return false;
    if (Array.isArray(field)) return field.length > 0;
    if (typeof field === "string") return field.trim().length > 0;
    if (typeof field === "object") return Object.keys(field).length > 0;
    return Boolean(field);
  };
  return [
    profile.what_they_sell,
    profile.business_health,
    profile.top_priorities,
    profile.top_challenges,
    profile.top_buying_signals,
    profile.executive_conversation_themes,
    profile.competitive_pressure,
  ].some(hasContent);
};

export const ACCOUNT_REVENUE_UNAVAILABLE = "Not Identified";

/** Formatted revenue with optional " Revenue" suffix; 0/null → "Not Identified". */
export const formatAccountRevenueLabel = (
  value,
  { withSuffix = true, fallback = ACCOUNT_REVENUE_UNAVAILABLE } = {},
) => {
  const formatted = formatRevenueShort(value, { fallback });
  if (!formatted || formatted === fallback) return fallback;
  return withSuffix ? `${formatted} Revenue` : formatted;
};

// get match score variant
export const matchScoreVariant = (score, { leadScale = false } = {}) => {
  if (typeof score !== "number" || Number.isNaN(score)) return "neutral";
  if (leadScale) return getVariant(score);
  return getVariant(score);
};

// normalize match score for fit
export const normalizeMatchScoreForFit = (score, { assumeSmallScaleBand = false } = {}) => {
  let n = Number(score);
  if (!Number.isFinite(n)) return null;
  if (
    assumeSmallScaleBand &&
    Number.isInteger(n) &&
    n >= 1 &&
    n <= 5
  ) {
    n *= 20;
  }
  return Math.min(100, Math.max(0, n));
};

/**
 * Single source of truth for match-score bands (0-100). The server's contact filters and
 * grouping (contacts.model.js) use the same cut-offs; keep them in step.
 */
export const MATCH_SCORE_FIT_THRESHOLDS = Object.freeze({ strong: 81, moderate: 50 });

const classifyMatchFitPct = (pct) => {
  if (pct >= MATCH_SCORE_FIT_THRESHOLDS.strong) return { key: "strong", label: "Strong Fit" };
  if (pct >= MATCH_SCORE_FIT_THRESHOLDS.moderate) return { key: "moderate", label: "Medium Fit" };
  return { key: "not_fit", label: "No Fit" };
};

/** Aggregated contact match score is stored as 0–100 (same scale as per-campaign lead scoring). */
export const normalizeAggregatedLeadMatchScoreForBands = (score) => {
  let n = Number(score);
  if (!Number.isFinite(n)) return null;
  return Math.min(100, Math.max(0, n));
};

/** Category for the aggregated contact-level match score (listing, filters, grouping). */
export const getAggregateMatchFitMeta = (score) => {
  const pct = normalizeAggregatedLeadMatchScoreForBands(score);
  if (pct == null) return null;
  return { ...classifyMatchFitPct(pct), pct };
};

// get match score fit meta
export const getMatchScoreFitMeta = (
  score,
  { assumeSmallScaleBand = false } = {},
) => {
  const pct = normalizeMatchScoreForFit(score, { assumeSmallScaleBand });
  if (pct == null) return null;
  const { key, label } = classifyMatchFitPct(pct);
  return { key, label, pct };
};

// get match score fit pill tone classes
export const matchScoreFitPillToneClasses = (fitKey) => {
  const map = {
    strong:
      "bg-success-50 text-success-500 ring-success-500/25 [--fit-dot:text-success-500]",
    moderate:
      "bg-warning-50 text-warning-500 ring-warning-500/25 [--fit-dot:text-warning-500]",
    not_fit:
      "bg-error-50 text-error-500 ring-error-500/20 [--fit-dot:text-error-500]",
  };
  return map[fitKey] ?? map.not_fit;
};

/** Maps to filter param  */
export const MATCH_SCORE_FIT_BUCKET_LABELS = {
  strong: "Strong Fit",
  moderate: "Medium Fit",
  not_fit: "No Fit",
  unavailable: "Unavailable",
};

/** get match score fit bucket key */
export const getMatchScoreFitBucketKey = (score) => {
  const unavailable =
    score === "" ||
    score === null ||
    score === undefined ||
    (typeof score === "number" && (Number.isNaN(score) || score === 0));
  if (unavailable) return "unavailable";
  const meta = getAggregateMatchFitMeta(score);
  return meta?.key ?? "not_fit";
};

export const getContactMatchFitGroupLabel = (score) => {
  const k = getMatchScoreFitBucketKey(score);
  return MATCH_SCORE_FIT_BUCKET_LABELS[k] ?? MATCH_SCORE_FIT_BUCKET_LABELS.unavailable;
};

// get lead scale score pill classes
export const getLeadScaleScorePillClasses = (score) => {
  const v = matchScoreVariant(score, { leadScale: true });
  const map = {
    success:
      "bg-success-50 text-success-500 ring-success-500/25 shadow-sm shadow-success-500/10",
    warning:
      "bg-warning-50 text-warning-500 ring-warning-500/25 shadow-sm shadow-warning-500/10",
    danger:
      "bg-error-50 text-error-500 ring-error-500/20 shadow-sm shadow-error-500/10",
    neutral: "bg-gray-100 text-gray-600 ring-gray-300/50",
    lightBlue:
      "bg-[#207C971A] text-[#207C97] ring-[#207C97]/25 shadow-sm shadow-[#207C97]/10",
  };
  return map[v] ?? map.neutral;
};

// get match score fit key pill classes
export const getMatchScoreFitKeyPillClasses = (fitKey) => {
  const map = {
    strong:
      "bg-success-50 text-success-500 ring-success-500/25 shadow-sm shadow-success-500/10",
    moderate:
      "bg-warning-50 text-warning-500 ring-warning-500/25 shadow-sm shadow-warning-500/10",
    not_fit:
      "bg-error-50 text-error-500 ring-error-500/20 shadow-sm shadow-error-500/10",
  };
  return map[fitKey] ?? map.not_fit;
};



// get file size in KB/MB
export const formatFileSize = (size) => {
  if (size < 1024) return size + " bytes";
  if (size < 1048576) return (size / 1024).toFixed(2) + " KB";
  return (size / 1048576).toFixed(2) + " MB";
};

// get file icon
export const getFileIcon = (fileType) => {
  if (fileType.startsWith("image/"))
    return <ImageIcon className="text-secondary w-3.5 h-3.5" />;
  if (fileType.startsWith("video/"))
    return <Video className="text-secondary w-3.5 h-3.5" />;
  if (fileType.includes("pdf"))
    return <FileText className="text-secondary w-3.5 h-3.5" />;
  return <FileIcon className="text-secondary w-3.5 h-3.5" />;
};

// formatted date return in current locale
export function formatTimeAgo(input) {
  const parsedDate = new Date(input);
  if (isNaN(parsedDate)) return "Invalid date";

  const now = new Date();
  const diffInSeconds = Math.floor((now - parsedDate) / 1000);

  if (diffInSeconds < 60) return "just now";

  const minutes = Math.floor(diffInSeconds / 60);
  if (minutes < 60) return `${minutes} minute${minutes !== 1 ? "s" : ""} ago`;

  const hours = Math.floor(diffInSeconds / 3600);
  if (hours < 24) return `${hours} hour${hours !== 1 ? "s" : ""} ago`;

  const days = Math.floor(diffInSeconds / 86400);
  if (days < 30) return `${days} day${days !== 1 ? "s" : ""} ago`;

  return parsedDate.toLocaleDateString("en-US", {
    month: "long",
    year: "numeric",
    day: "numeric",
  });
}

// Helper function to set user data and navigate
export const handleAuthSuccess = async (
  userData,
  token,
  message,
  setUser,
  navigate,
  isSignup = false,
  onboardFlag = false,
  silent = false,
  options = {}
) => {
  const {
    code,
    hasAccessRequest: hasAccessRequestOpt = false,
    role,
    requestTarget,
    orgName,
    requestSubmittedAt,
    approvalStatus: approvalStatusOptCamel,
    approval_status: approvalStatusOptSnake,
    rejectedBy,
    organization_llm_type: organizationLlmTypeOptSnake,
    organizationLlmType: organizationLlmTypeOptCamel,
  } = options;
  const approvalFromApi =
    approvalStatusOptSnake ?? approvalStatusOptCamel ?? undefined;
  const isPendingApproval =
    code === "pending_approval" ||
    (!isSignup && approvalFromApi === "pending");
  const isRejected =
    code === "access_rejected" || approvalFromApi === "rejected";

  const resolvedRole = role || userData.role || USER_ROLES.USER;

  const approvalStatus = isRejected
    ? "rejected"
    : isPendingApproval
      ? "pending"
      : isSignup
        ? null
        : approvalFromApi != null && approvalFromApi !== ""
          ? approvalFromApi
          : !onboardFlag
            ? null
            : "approved";

  const hasAccessRequest =
    isPendingApproval && hasAccessRequestOpt === true;

  const userPatch = {
    id: userData.id,
    name: userData.name,
    email: userData.email,
    organization_id:
      userData.organization_id ?? userData.organizationId ?? null,
    profileImage: null,
    isOnboarded: onboardFlag,
    role: resolvedRole,
    approvalStatus,
    hasAccessRequest,
    organization_llm_type:
      userData.organization_llm_type ??
      userData.organizationLlmType ??
      organizationLlmTypeOptSnake ??
      organizationLlmTypeOptCamel ??
      null,
    ...(isSignup && { pricingPageCompleted: false }),
    ...(requestTarget != null && { requestTarget }),
    ...(orgName != null && { orgName }),
    ...(requestSubmittedAt != null && { requestSubmittedAt }),
    ...(rejectedBy != null && { rejectedBy }),
    ...(userData.plan !== undefined && { plan: userData.plan }),
    ...(userData.trial_approved_at !== undefined && {
      trial_approved_at: userData.trial_approved_at,
    }),
    ...(userData.trial_ends_at !== undefined && {
      trial_ends_at: userData.trial_ends_at,
    }),
    ...(options.plan !== undefined && { plan: options.plan }),
    ...(options.trial_approved_at !== undefined && {
      trial_approved_at: options.trial_approved_at,
    }),
    ...(options.trial_ends_at !== undefined && {
      trial_ends_at: options.trial_ends_at,
    }),
  };

  const mergedForRoute = {
    ...userPatch,
  };

  try {
    setUser(userPatch);
    localStorage.setItem("token", token);
    window.dispatchEvent(new Event("tokenUpdated"));

    if (!silent) {
      showSuccess(message);
    }

    const target = getTargetRoute(mergedForRoute);

    if (isSignup) {
      navigate("/onboarding", { replace: true });
      return;
    }

    navigate(target, { replace: true });
  } catch (err) {
    console.error("handleAuthSuccess failed:", err);
    localStorage.removeItem("token");
    window.dispatchEvent(new Event("tokenUpdated"));
    showError("Something went wrong signing you in. Please try again.");
  }
};

// contact format data helper function
export const transformContactData = (contact, index) => {
  const rawLead = Array.isArray(contact.leadScore) ? contact.leadScore : [];
  const validLead = rawLead.filter(
    (row) =>
      row &&
      !row.error &&
      typeof row.match_score === "number" &&
      !Number.isNaN(row.match_score),
  );
  const sortedLead = [...validLead].sort(
    (a, b) => b.match_score - a.match_score,
  );
  const maxFromLead =
    validLead.length > 0
      ? Math.max(...validLead.map((r) => r.match_score))
      : null;

  const legacyRaw = contact.matchScore;
  const legacyNum =
    typeof legacyRaw === "number"
      ? legacyRaw
      : legacyRaw === "" || legacyRaw == null
        ? null
        : Number(legacyRaw);
  const legacyOk =
    legacyNum != null && !Number.isNaN(legacyNum) ? legacyNum : null;

  const matchScore =
    maxFromLead != null ? maxFromLead : legacyOk != null ? legacyOk : "";

  const matchFitGroupLabel = getContactMatchFitGroupLabel(matchScore);

  return {
    id: contact.id || `contact-${Date.now()}-${index}`,
    name: contact.full_name || "",
    email: contact.email || "",
    company: contact.company || "",
    matchScore,
    matchFitGroupLabel,
    matchScoreReason: contact.matchScoreReason ?? "",
    leadScoreBreakdown: sortedLead.map((r) => ({
      campaignId: r.campaign_id ?? r.id ?? "",
      campaignName: r.campaign_name ?? "",
      matchScore: r.match_score,
      reasoning: r.reasoning ?? "",
    })),
    position: contact.designation || "",
    location: contact.hqCountry || contact.country || contact.location || "",
    targetIndustries: contact.targetIndustries || contact.industry || "",
    linkedinUrl: contact.linkedin_url || "",
    headline: contact.headline || "",
    emailStatus: contact.email_status || null,
    whatsappNumber: contact.whatsapp_number || contact?.number || contact?.whatsappNumber || contact?.contactNumber || "N/A",
    companyLinkedinUrl: contact.company_linkedin_url || "",
    companyWebsite: contact.company_website || "",
    leadSource: contact.lead_source || "",
    socialLeadId: contact.social_lead_id || "",
    emailOptOut: Boolean(contact.email_opt_out),
    emailConsentStatus: contact.email_consent_status || (contact.email_opt_out ? "unsubscribed" : "unknown"),
    emailConsentSource: contact.email_consent_source || null,
    emailConsentAt: contact.email_consent_at || null,
    buyingRole: contact.buying_role || "",
    persona: contact.persona || "",
    description: contact.description || "",
    tags: contact.tags || [],
    ownerId: contact.user_id || "",
    createdAt:
      formatDate(contact.created_at) || formatDate(new Date().toISOString()),
    updatedAt:
      formatDate(contact.updated_at) || formatDate(new Date().toISOString()),
    salesScore: contact.sales_score || 0,
    currentlyEnrolledCampaignName: contact.currentlyEnrolledCampaignName || contact.currently_enrolled_campaign_name || "Not Enrolled",
    industry: contact.industry || "Not Available",
    engagementScore: contact?.campaignEngagement?.[0]?.runtimeEngagementScore ?? "N/A",
    engagementScoreCampaignName: contact?.campaignEngagement?.[0]?.campaignName || "N/A",
    engagementScoreReason: contact.campaignEngagement?.[0]?.runtimeEngagementBreakdown || "N/A",
    companySize: contact.company_size !== "" ? contact.company_size : "N/A",
  };
};

// format date to "MM/DD/YYYY"
export const formatDate = (isoString) => {
  const date = new Date(isoString);

  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "2-digit",
    year: "numeric",
  });
};

export const isSchedulePast = (isoString) => {
  const date = new Date(isoString);
  if (Number.isNaN(date.getTime())) return false;
  return date.getTime() < Date.now();
};

export const formatTodayOrTomorrow = (isoString) => {
  const date = new Date(isoString);
  if (Number.isNaN(date.getTime())) return formatDate(isoString);

  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const tomorrow = new Date(today);
  tomorrow.setDate(tomorrow.getDate() + 1);
  const target = new Date(date.getFullYear(), date.getMonth(), date.getDate());

  if (target.getTime() === today.getTime()) return "today";
  if (target.getTime() === tomorrow.getTime()) return "tomorrow";
  return formatDate(isoString);
};

const getDomainLabelFromUrl = (url = "") => {
  try {
    let urlToProcess = String(url).trim();
    if (!urlToProcess.startsWith("http://") && !urlToProcess.startsWith("https://")) {
      urlToProcess = `https://${urlToProcess}`;
    }
    return new URL(urlToProcess).hostname.replace(/^www\./i, "");
  } catch {
    return "";
  }
};

/** Parse citation strings like `([domain.com](https://domain.com/path))` for link UI. */
export const parseCitationLink = (citation = "") => {
  if (!citation || typeof citation !== "string") {
    return { href: "", label: "" };
  }

  let text = citation.trim();
  while (text.startsWith("(") && text.endsWith(")")) {
    text = text.slice(1, -1).trim();
  }

  const markdownMatch = text.match(/\[([^\]]*)\]\(([^)]+)\)/);
  if (markdownMatch) {
    const href = markdownMatch[2].trim();
    return {
      href,
      label: getDomainLabelFromUrl(href) || markdownMatch[1].trim(),
    };
  }

  const bareUrlMatch = text.match(/https?:\/\/[^\s)]+/i);
  if (bareUrlMatch) {
    const href = bareUrlMatch[0];
    return { href, label: getDomainLabelFromUrl(href) };
  }

  const href = text.startsWith("http") ? text : `https://${text}`;
  return {
    href,
    label: getDomainLabelFromUrl(href) || text,
  };
};

/** Split body text from trailing/inline markdown citation links. */
export const parseTextWithInlineCitation = (input = "") => {
  if (!input || typeof input !== "string") {
    return { text: "", href: "", label: "" };
  }

  const text = String(input).trim();
  const markdownMatch = text.match(/\[([^\]]*)\]\(([^)]+)\)/);

  if (!markdownMatch) {
    return { text, href: "", label: "" };
  }

  const href = markdownMatch[2].trim();
  const label = getDomainLabelFromUrl(href) || markdownMatch[1].trim();
  const mainText = text
    .replace(/\s*\(\s*\[[^\]]*\]\([^)]+\)\s*\)\s*/g, " ")
    .replace(/\s*\[[^\]]*\]\([^)]+\)\s*/g, " ")
    .replace(/\(\s*\)/g, "")
    .replace(/\s{2,}/g, " ")
    .trim();

  return { text: mainText, href, label };
};

/** Origin-only URL; accepts plain URLs and markdown citations. */
export const trimToFirstSlash = (url) => {
  if (!url || typeof url !== "string" || url.trim() === "") {
    return "";
  }

  const { href } = parseCitationLink(url);
  const urlToProcess = href || url.trim();

  try {
    let normalized = urlToProcess;
    if (!normalized.startsWith("http://") && !normalized.startsWith("https://")) {
      normalized = `https://${normalized}`;
    }
    const { protocol, host } = new URL(normalized);
    return `${protocol}//${host}`;
  } catch {
    const domain = getDomainLabelFromUrl(urlToProcess);
    return domain ? `https://${domain}` : "";
  }
};

// get connected channels map
export const getConnectedChannelsMap = (channels = []) => {
  const map = {
    whatsapp: false,
    linkedin: false,
    outlook: false,
    google_oauth: false,
    crm: false,
    mail: false,
  };

  channels.forEach((ch) => {
    if (ch.status === "Active") {
      map[ch.channel.toLowerCase()] = true;
    }
  });

  return map;
};

// get avatar color based on name
export const getAvatarColor = (str) => {
  if (!str) return "bg-gray-200 text-gray-600 dark:bg-gray-700 dark:text-gray-300";

  // Generate a hash from the string
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }

  // Use hash to select from predefined color palette
  // Pastel in light mode; a tinted wash with a bright initial in dark mode.
  const colors = [
    { bg: "bg-secondary", text: "text-white" },
    { bg: "bg-green-100 dark:bg-green-400/15", text: "text-success-500 dark:text-green-300" },
    { bg: "bg-purple-100 dark:bg-purple-400/15", text: "text-purple-500 dark:text-purple-300" },
    { bg: "bg-pink-100 dark:bg-pink-400/15", text: "text-pink-500 dark:text-pink-300" },
    { bg: "bg-indigo-100 dark:bg-indigo-400/15", text: "text-indigo-500 dark:text-indigo-300" },
    { bg: "bg-teal-100 dark:bg-teal-400/15", text: "text-teal-500 dark:text-teal-300" },
    { bg: "bg-orange-100 dark:bg-orange-400/15", text: "text-orange-500 dark:text-orange-300" },
    { bg: "bg-red-100 dark:bg-red-400/15", text: "text-error-500 dark:text-red-300" },
    { bg: "bg-cyan-100 dark:bg-cyan-400/15", text: "text-cyan-500 dark:text-cyan-300" },
    { bg: "bg-amber-100 dark:bg-amber-400/15", text: "text-amber-500 dark:text-amber-300" },
  ];

  const colorIndex = Math.abs(hash) % colors.length;
  return `${colors[colorIndex].bg} ${colors[colorIndex].text}`;
};


// Get current time in a specific timezone
export const getNowInTimezone = (timezone) => {
  return dayjs.tz(dayjs(), timezone);
};

// Check if a date is in the past 
export const isDateInPast = (date, timezone) => {
  const today = getNowInTimezone(timezone).startOf("day");
  const dateToCheck = dayjs.tz(date, timezone).startOf("day");
  return dateToCheck.isBefore(today, "day");
};

// Validate and get the correct date/time combination
export const getValidDateTime = (date, time, timezone) => {
  if (!date || !time) {
    const now = getNowInTimezone(timezone);
    return { date: now, combined: now, wasAdjusted: false };
  }

  const now = getNowInTimezone(timezone);
  const today = now.startOf("day");

  // Ensure date is in the correct timezone
  const dateInTz = dayjs.isDayjs(date)
    ? date.tz(timezone)
    : dayjs.tz(date, timezone);
  const selectedDay = dateInTz.startOf("day");

  // Parse the selected time with the given date 
  let combined = dayjs.tz(
    `${dateInTz.format("YYYY-MM-DD")} ${time}`,
    "YYYY-MM-DD hh:mm A",
    timezone
  );

  // Check if this is today and if the time is in the past
  const isToday = selectedDay.isSame(today, "day");
  const isPastTime = combined.isBefore(now);

  if (isToday && isPastTime) {
    //  Move to next day with the same time
    const nextDay = dateInTz.add(1, "day");
    combined = dayjs.tz(
      `${nextDay.format("YYYY-MM-DD")} ${time}`,
      "YYYY-MM-DD hh:mm A",
      timezone
    );
    return { date: nextDay, combined, wasAdjusted: true };
  }

  return { date: dateInTz, combined, wasAdjusted: false };
};


// get date picker class name
export const getDatePickerClassName = (hasError) => {
  const baseClasses = `
    w-[220px] text-gray-700 !shadow-none !text-xs
    [&_[data-slot=input-wrapper]]:border
    [&_[data-slot=input-wrapper]]:border-gray-300
    [&_[data-slot=input-wrapper]]:rounded-lg
    [&_[data-slot=input-wrapper]]:hover:border-gray-400
    [&_[data-hovered]]:bg-secondary/20
    [&_[data-today]]:border-secondary
    [&_[data-slot=segment]]:!text-xs
    [&_[data-slot=segment]]:!text-gray-700
    [&_svg]:!text-gray-500
    [&_button_svg]:!text-gray-500
    [&_[data-slot=calendar-icon]]:!text-gray-500
    [&_button]:!text-gray-500
  `;

  const errorClasses = hasError
    ? `
      [&_[data-slot=input-wrapper]]:!border-error-500
      [&_[data-slot=input-wrapper]]:!border-1
      [&_[data-slot=input-wrapper]]:focus-within:!border-error-500
      [&_[data-slot=input-wrapper]]:hover:!border-error-500
      [&_[data-slot=error-message]]:!text-error-500 !text-xs !font-inter !font-normal
      [&_[data-slot=segment]]:!text-error-500
      [&_[data-slot=segment]]:!text-xs
      [&_[data-slot=segment][data-invalid=true]]:!text-error-500
      [&_[data-slot=segment][data-editable=true]]:!text-error-500
      [&_svg]:!text-error-500
      [&_button_svg]:!text-error-500
      [&_[data-slot=calendar-icon]]:!text-error-500
      [&_button]:!text-error-500
    `
    : "";

  return baseClasses + errorClasses;
};


// get campaign step icons 
export const getChannelIcon = ({ channel, isCircular = false, size = 20, iconColor = "#207C97" }) => {
  switch (channel) {
    case "email":
      return <Mail size={size} color={iconColor} />;
    case "whatsapp":
      return <img src={whatsappIcon} alt="WhatsApp" width={size} height={size} />;
    case "phone":
    case "voice":
      return <Phone size={size} color={iconColor} />;
    case "linkedin":
      return (
        <img
          src={isCircular ? LinkedinCircular : LinkedinSquare}
          alt="LinkedIn"
          width={size}
          height={size}

          style={{ display: "inline-block", verticalAlign: "middle" }}
        />
      );
    default:
      return <Mail size={size} color={iconColor} />;
  }
};


// get actions for condition node
export const getActionLabel = (action) => {

  switch (action) {
    case "email_send":
    case "followup_email":
      return action?.email_subject?.charAt(0).toUpperCase() + action?.email_subject?.slice(1) || action?.replace(/_/g, " ")?.charAt(0).toUpperCase() + action?.replace(/_/g, " ")?.slice(1) || "Initial Outreach";
    case "whatsapp_send":
    case "whatsapp_message":
    case "whatsapp_sms":
      return "WhatsApp Message";
    case "phone_call":
    case "voice_call":
    case "voice_ai":
      return "Voice Call";
    case "connect":
    case "linkedin_connect":
      return "LinkedIn Connect";
    case "message":
    case "linkedin_sms":
      return "LinkedIn Message";
    case "linkedin_like":
    case "like_post":
    case "post_like":
    case "like_latest_post":
      return "LinkedIn Like";
    default:
      return action?.action?.replace(/_/g, " ")?.charAt(0).toUpperCase() + action?.action?.replace(/_/g, " ")?.slice(1) || "Action";
  }
};


// get duration info
export const getDurationInfo = (duration = {}) => {
  if (!duration || typeof duration !== "object") {
    return { value: 0, unit: "", label: "" };
  }

  const units = [
    { key: "days", singular: "Day", plural: "Days" },
    { key: "hours", singular: "Hour", plural: "Hours" },
    { key: "minutes", singular: "Minute", plural: "Minutes" },
  ];

  for (const { key, singular, plural } of units) {
    const value = duration[key];
    if (value && Number(value) > 0) {
      return {
        value: Number(value),
        unit: singular,
        label: `${value} ${value > 1 ? plural : singular}`,
      };
    }
  }

  return { value: 0, unit: "", label: "" };
};

// format step date
export const formatStepDate = (dateString) => {
  if (!dateString) return null;

  const date = new Date(dateString);

  const time = date.toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });

  const formattedDate = date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });

  return `${time} | ${formattedDate}`;
};

/** Resolves a timestamp for a step key from step data */
export function getStepTimestamp(data, key) {
  if (!data || !key) return null;
  const connectionState = data.aiTemplate?.signals?.connection_state ?? data.signals?.connection_state;
  if (key === "connect_sent_at" && connectionState === "already_connected") {
    return data.aiTemplate?.connected_at ?? data.connected_at ?? null;
  }
  const v = data.aiTemplate?.[key] ?? data[key];
  if (
    key === "message_delivered_at" &&
    (v == null || v === "")
  ) {
    return data.aiTemplate?.message_read_at ?? data.message_read_at ?? null;
  }
  return v == null || v === "" ? null : v;
}

/** Tooltip: pending state uses pendingLabel when no timestamp; done state shows label + time. */
export function getChannelStepLabel(stepDef, data) {
  const ts = getStepTimestamp(data, stepDef.key);
  const connectionState = data?.aiTemplate?.signals?.connection_state ?? data?.signals?.connection_state;
  const linkedinGate = data?.aiTemplate?.signals?.linkedin_gate ?? data?.signals?.linkedin_gate;
  if (
    linkedinGate?.status === "waiting_for_acceptance" &&
    stepDef.key === "message_sent_at"
  ) {
    return "Waiting for LinkedIn connection acceptance";
  }
  if (stepDef.key === "connect_sent_at" && connectionState === "already_connected") {
    const when = formatStepDate(ts);
    return when ? `Already connected at ${when}` : "Already connected";
  }
  if (stepDef.key === "connected_at" && connectionState === "already_connected") {
    const when = formatStepDate(ts);
    return when ? `Existing connection confirmed at ${when}` : "Existing connection confirmed";
  }
  if (!ts) {
    return stepDef.pendingLabel ?? stepDef.label;
  }
  const when = formatStepDate(ts);
  return when ? `${stepDef.label} at ${when}` : stepDef.label;
}

// get current step from data
export const getCurrentStepFromData = (channel, data) => {
  const steps = CHANNEL_STEPS[channel] || [];
  let currentStep = 0;

  for (let i = 0; i < steps.length; i++) {
    if (getStepTimestamp(data, steps[i].key)) {
      currentStep = i + 1;
    } else {
      break;
    }
  }

  return currentStep;
};

// get Text in capitalize
export const getTextInCapitalize = (text) => {
  if (!text || typeof text !== "string") return "";
  return text.charAt(0).toUpperCase() + text.slice(1);
};

const TRUNCATE_SUFFIX = "...";

// truncate text
export const truncateText = (text, maxLength = 50) => {
  if (text == null || text === "") return "";

  const value = String(text);
  const limit = Number(maxLength);

  if (!Number.isFinite(limit) || limit <= 0) return TRUNCATE_SUFFIX;
  if (value.length <= limit) return value;

  return `${value.slice(0, limit).trimEnd()}${TRUNCATE_SUFFIX}`;
}


// convert date to string format "YYYY-MM-DD"
export const toDateStr = (date) => {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
}

// get color based on value
export const getTextColorBasedOnValue = (value) => {
  if (value < 25) return "text-error-500";
  if (value < 50) return "text-warning-500";
  if (value === "N/A") return "text-gray-500";
  return "text-success-500";
};

// get campaign insight theme
export const getCampaignInsightTheme = (textColor) => {
  const themes = {
    "text-error-500": {
      textClass: "text-error-500",
      iconBgClass: "bg-error-500/15",
      badgeBgClass: "bg-error-500/10",
      progressColor: "bg-error-500",
    },
    "text-warning-500": {
      textClass: "text-warning-500",
      iconBgClass: "bg-warning-500/15",
      badgeBgClass: "bg-warning-500/10",
      progressColor: "bg-warning-500",
    },
    "text-success-500": {
      textClass: "text-success-500",
      iconBgClass: "bg-success-500/15",
      badgeBgClass: "bg-success-500/10",
      progressColor: "bg-success-500",
    },
    "text-gray-500": {
      textClass: "text-gray-500",
      iconBgClass: "bg-gray-100",
      badgeBgClass: "bg-gray-100",
      progressColor: "bg-gray-400",
    },
  };
  if (textColor && themes[textColor]) return themes[textColor];
  return {
    textClass: textColor || "text-gray-600",
    iconBgClass: "bg-gray-100",
    badgeBgClass: "bg-gray-100",
    progressColor: "bg-secondary",
  };
};

// logout user
export const handleReturnToSignUp = (navigate) => {
  localStorage.removeItem("token");
  window.dispatchEvent(new Event("tokenUpdated"));
  navigate("/signup");
};

// get linkedin url resolved
export const getLinkedinUrl = (input = "") => {
  if (!input) return "";

  try {
    let url = input.trim();

    // Add protocol if missing
    if (!/^https?:\/\//i.test(url)) {
      url = "https://" + url;
    }

    const parsed = new URL(url);

    const hostname = "www.linkedin.com";

    let path = parsed.pathname;

    // Ensure it contains /in/
    if (!/^\/in\//i.test(path)) {
      // If someone passed just username
      path = `/in/${path.replace(/^\/+/, "")}`;
    }

    // Remove extra slashes and ensure trailing slash
    path = path.replace(/\/+$/, "") + "/";

    return `https://${hostname}${path}`;
  } catch (e) {
    // Fallback (if URL parsing fails)
    const clean = input
      .replace(/^https?:\/\//, "")
      .replace(/^www\./, "")
      .replace(/linkedin\.[^/]+\//, "")
      .replace(/^\/+/, "")
      .replace(/\/+$/, "");

    return `https://www.linkedin.com/in/${clean}/`;
  }
};

export const renderTextWithLinks = (text) => {
  if (typeof text !== "string") return text;

  // Match markdown links e.g. ([label](url))[label], ([label](url)), [label](url), or raw URLs
  const markdownRegex =
    /\(?\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)\)?(?:\[[^\]]+\])?|(https?:\/\/[^\s)]+)/gi;

  const parts = [];
  let lastIndex = 0;
  let match;

  while ((match = markdownRegex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push(text.slice(lastIndex, match.index));
    }

    const markdownLabel = match[1];
    const markdownUrl = match[2];
    const rawUrl = match[3];

    let url = markdownUrl || rawUrl;
    let suffix = "";

    if (rawUrl) {
      const pMatch = url.match(/([.,;)]+)$/);
      if (pMatch) {
        url = url.slice(0, -pMatch[0].length);
        suffix = pMatch[0];
      }
    }

    let displayLabel = "";
    if (markdownLabel) {
      displayLabel =
        markdownLabel.startsWith("[") && markdownLabel.endsWith("]")
          ? markdownLabel
          : `[${markdownLabel}]`;
    } else {
      let label = url;
      try {
        const parsed = new URL(url);
        const filename = parsed.pathname.split("/").pop();
        if (filename && filename.toLowerCase().endsWith(".pdf")) {
          label = decodeURIComponent(filename);
        } else {
          label = parsed.hostname.replace(/^www\./i, "");
        }
      } catch (e) {
        label = url;
      }
      displayLabel = label;
    }

    parts.push(
      <React.Fragment key={match.index}>
        <a
          href={url}
          target="_blank"
          rel="noreferrer"
          className="text-secondary-dark hover:underline break-all font-medium inline"
          style={{ color: "#207C97" }}
          onClick={(e) => e.stopPropagation()}
        >
          {displayLabel}
        </a>
        {suffix}
      </React.Fragment>,
    );

    lastIndex = markdownRegex.lastIndex;
  }

  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }

  return parts.length > 0 ? parts : text;
};

export const convertCurrenciesInText = (text) => {
  if (!text || typeof text !== "string") return text;

  const regex1 = /(?:(₹|INR|Rs\.?|€|EUR|£|GBP)\s*(\d+(?:,\d+)*(?:\.\d+)?)\s*(trillion|billions?|millions?|crores?|lakhs?|thousands?|tn|bn|mn|cr|[tbmk])?\b)/gi;
  const regex2 = /\b(\d+(?:,\d+)*(?:\.\d+)?)\s*(trillion|billions?|millions?|crores?|lakhs?|thousands?|tn|bn|mn|cr|[tbmk])?\s*(Rupees|Euros|Pounds|INR|EUR|GBP|Rs\.?|₹|€|£)\b/gi;

  let result = text;

  const replaceFn = (match, currencyMatch, amountStr, unitStr) => {
    let cleanCurrency = (currencyMatch || "").trim().toUpperCase();
    if (!cleanCurrency) return match;

    let symbol = "$";
    if (cleanCurrency.includes("₹") || cleanCurrency.includes("INR") || cleanCurrency.includes("RS") || cleanCurrency.includes("RUPEES")) {
      symbol = "₹";
    } else if (cleanCurrency.includes("€") || cleanCurrency.includes("EUR") || cleanCurrency.includes("EUROS")) {
      symbol = "€";
    } else if (cleanCurrency.includes("£") || cleanCurrency.includes("GBP") || cleanCurrency.includes("POUNDS")) {
      symbol = "£";
    } else if (cleanCurrency.includes("$") || cleanCurrency.includes("USD") || cleanCurrency.includes("DOLLARS")) {
      symbol = "$";
    }

    const amount = parseFloat(amountStr.replace(/,/g, ""));
    if (isNaN(amount) || amount <= 0) return match;

    const unit = (unitStr || "").toLowerCase();
    let millions = amount;
    if (unit && REVENUE_UNIT_TO_MILLIONS[unit] !== undefined) {
      millions = amount * REVENUE_UNIT_TO_MILLIONS[unit];
    } else if (amount >= 100000) {
      millions = amount / 1_000_000;
    }

    if (symbol === "$") {
      return formatMillionsShort(millions, "$") || match;
    }

    let usdMillions = millions;
    if (symbol === "₹") {
      usdMillions = millions / 83;
    } else if (symbol === "€") {
      usdMillions = millions * 1.1;
    } else if (symbol === "£") {
      usdMillions = millions * 1.25;
    }

    const shortForm = formatMillionsShort(usdMillions, "$");
    return shortForm || match;
  };

  result = result.replace(regex1, (match, p1, p2, p3) => replaceFn(match, p1, p2, p3));
  result = result.replace(regex2, (match, p1, p2, p3) => replaceFn(match, p3, p1, p2));

  return result;
};

export const convertCurrenciesDeep = (obj) => {
  if (obj == null) return obj;
  if (typeof obj === "string") {
    return convertCurrenciesInText(obj);
  }
  if (Array.isArray(obj)) {
    return obj.map(convertCurrenciesDeep);
  }
  if (typeof obj === "object") {
    const next = {};
    for (const key of Object.keys(obj)) {
      next[key] = convertCurrenciesDeep(obj[key]);
    }
    return next;
  }
  return obj;
};
